import { useState, useEffect, useCallback } from 'react';
import {
  useSubscribers,
  useCreateSubscriber,
  useUpdateSubscriber,
  useDeleteSubscriber,
} from '../../../hooks/useSubscribers';
import { useNationalities } from '../../../hooks/useNationalities';
import { useMaritalStatuses } from '../../../hooks/useMaritalStatuses';
import { useIvaConditions } from '../../../hooks/useIvaConditions';
import { useCountries } from '../../../hooks/useCountries';
import { useProvinces } from '../../../hooks/useProvinces';
import { useDepartments } from '../../../hooks/useDepartments';
import { useDistricts } from '../../../hooks/useDistricts';
import { ITEMS_PER_PAGE, INITIAL_SUBSCRIBER_STATE } from '../constants/subscribersConstants';
import { filterSubscribers } from '../utils/subscribersUtils';
import { usePaginatedFilter } from '../../../common/hooks/usePaginatedFilter';

export const useSubscribersTable = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedSubscriber, setSelectedSubscriber] = useState(INITIAL_SUBSCRIBER_STATE);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [subscriberToDelete, setSubscriberToDelete] = useState(null);

  const { data: subscribersData = [], isLoading: subscribersLoading } = useSubscribers();
  const { data: nationalities = [] } = useNationalities();
  const { data: maritalStatuses = [] } = useMaritalStatuses();
  const { data: ivaConditions = [] } = useIvaConditions();
  const { data: countries = [] } = useCountries();
  const { data: provinces = [] } = useProvinces();
  const { data: departments = [] } = useDepartments();
  const { data: districts = [] } = useDistricts();

  const createMutation = useCreateSubscriber();
  const updateMutation = useUpdateSubscriber();
  const deleteMutation = useDeleteSubscriber();

  const {
    data,
    totalCount,
    totalPages,
    page,
    rowsPerPage,
    searchTerm,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePaginatedFilter({
    data: subscribersData,
    itemsPerPage: ITEMS_PER_PAGE,
    filterFn: filterSubscribers,
  });

  const isLoading =
    subscribersLoading ||
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  const handleOpenDialog = useCallback(() => {
    setSelectedSubscriber(INITIAL_SUBSCRIBER_STATE);
    setEditMode(false);
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedSubscriber(INITIAL_SUBSCRIBER_STATE);
  }, []);

  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setSelectedSubscriber((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleSelectChange = useCallback((event) => {
    const { name, value } = event.target;

    setSelectedSubscriber((prev) => {
      const newState = { ...prev, [name]: value };

      if (name === 'uuid_country') {
        newState.uuid_province = '';
        newState.uuid_department = '';
        newState.uuid_district = '';
      } else if (name === 'uuid_province') {
        newState.uuid_department = '';
        newState.uuid_district = '';
      } else if (name === 'uuid_department') {
        newState.uuid_district = '';
      }

      return newState;
    });
  }, []);

  const handleSave = useCallback(async () => {
    try {
      if (editMode) {
        await updateMutation.mutateAsync(selectedSubscriber);
      } else {
        await createMutation.mutateAsync(selectedSubscriber);
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving subscriber:', error);
    }
  }, [editMode, selectedSubscriber, updateMutation, createMutation, handleCloseDialog]);

  const handleEdit = useCallback(
    (subscriber) => {
      let updatedSubscriber = { ...subscriber };

      if (subscriber.uuid_district) {
        const selectedDistrict = districts.find((d) => d.uuid === subscriber.uuid_district);
        if (selectedDistrict) {
          const selectedDepartment = departments.find(
            (d) => d.uuid === selectedDistrict.uuid_department
          );
          if (selectedDepartment) {
            const selectedProvince = provinces.find(
              (p) => p.uuid === selectedDepartment.uuid_province
            );
            if (selectedProvince) {
              const selectedCountry = countries.find(
                (c) => c.uuid === selectedProvince.uuid_country
              );

              updatedSubscriber = {
                ...updatedSubscriber,
                uuid_department: selectedDepartment.uuid,
                uuid_province: selectedProvince.uuid,
                uuid_country: selectedCountry?.uuid,
              };
            }
          }
        }
      }

      setSelectedSubscriber(updatedSubscriber);
      setEditMode(true);
      setDialogOpen(true);
    },
    [districts, departments, provinces, countries]
  );

  const handleDelete = useCallback(
    (uuid) => {
      const subscriber = subscribersData.find((s) => s.uuid === uuid);
      setSubscriberToDelete(subscriber);
      setDeleteConfirmOpen(true);
    },
    [subscribersData]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (subscriberToDelete) {
      try {
        await deleteMutation.mutateAsync(subscriberToDelete.uuid);
        setDeleteConfirmOpen(false);
        setSubscriberToDelete(null);
      } catch (error) {
        console.error('Error deleting subscriber:', error);
      }
    }
  }, [subscriberToDelete, deleteMutation]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    setSubscriberToDelete(null);
  }, []);

  useEffect(() => {
    if (
      !editMode &&
      dialogOpen &&
      nationalities.length > 0 &&
      maritalStatuses.length > 0 &&
      ivaConditions.length > 0
    ) {
      setSelectedSubscriber((prev) => ({
        ...prev,
        gender: 'h',
        uuid_nationality: nationalities[3]?.uuid || nationalities[0]?.uuid,
        uuid_marital_status: maritalStatuses[0]?.uuid,
        uuid_iva_condition: ivaConditions[0]?.uuid,
      }));
    }
  }, [editMode, dialogOpen, nationalities, maritalStatuses, ivaConditions]);

  return {
    data,
    isLoading,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    searchTerm,
    dialogOpen,
    editMode,
    selectedSubscriber,
    nationalities,
    maritalStatuses,
    ivaConditions,
    countries,
    provinces,
    departments,
    districts,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenDialog,
    handleCloseDialog,
    handleInputChange,
    handleSelectChange,
    handleSave,
    handleEdit,
    handleDelete,
    deleteConfirmOpen,
    subscriberToDelete,
    handleConfirmDelete,
    handleCancelDelete,
  };
};
